import React from "react";

import {
  AccordionSummary,
  AccordionDetails,
  AccordionProps as MuiAccordionProps,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import {
  AccordionCollapsedIcon,
  AccordionExpandedIcon,
  StyledAccordion,
} from "./Accordion.styles";
import { Typography } from "../Typography/Typography";
import { HELP_PAGE_URL } from "../../../utils/urlConstants";
import { ArrowIcon } from "../../../assets/iconComponents/ArrowIcon";

export const LINE_BREAK = <br />;

const getTypographySections = (
  content: React.ReactNode,
  isTitle: boolean,
  padding?: string,
): React.ReactElement => (
  <Typography
    key={content?.toString()}
    size={isTitle ? "large" : "medium"}
    isBold={isTitle}
    isCaps={false}
    isItalic={false}
    padding={padding}
    content={content}
  />
);

export const LinkedAccordian: React.FC<{
  id: string;
  panelKey: string;
  title: React.ReactNode;
  onClick: () => void;
  size: "small" | "large";
  padding?: string;
}> = ({ id, panelKey, title, onClick, size, padding }) => (
  <StyledAccordion expanded={false}>
    <AccordionSummary
      expandIcon={<ArrowIcon />}
      aria-controls={`${panelKey}-panel-content`}
      id={id}
      onClick={onClick}
    >
      {getTypographySections(title, size === "large", padding)}
    </AccordionSummary>
  </StyledAccordion>
);

export interface AccordionProps extends Omit<
  MuiAccordionProps,
  "children" | "content" | "title"
> {
  title: React.ReactNode;
  content: React.ReactNode;
  key: string;
  padding?: string;
}

export const Accordions: React.FC<{
  data: AccordionProps[];
  isLinked: boolean;
  size: "small" | "large";
  onClick?: (key: string) => void;
}> = ({ isLinked, size, data, onClick }) => {
  const [expanded, setExpanded] = React.useState<string | false>("");
  const history = useHistory();

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
      if (newExpanded && onClick) {
        onClick(panel);
      }
    };

  if (!isLinked) {
    const defaultAccordions = data.map((accordionSections) => (
      <StyledAccordion
        expanded={expanded === accordionSections.key}
        onChange={handleChange(accordionSections.key)}
        key={accordionSections.key}
      >
        <AccordionSummary
          expandIcon={
            expanded === accordionSections.key ? (
              <AccordionExpandedIcon />
            ) : (
              <AccordionCollapsedIcon />
            )
          }
          aria-controls={`${accordionSections.key}-panel-content`}
          id={`${accordionSections.key}-help`}
        >
          {getTypographySections(accordionSections.title, true)}
        </AccordionSummary>
        <AccordionDetails>
          {typeof accordionSections.content === "string"
            ? getTypographySections(
                accordionSections.content,
                false,
                accordionSections.padding,
              )
            : accordionSections.content}
        </AccordionDetails>
      </StyledAccordion>
    ));

    return <>{defaultAccordions}</>;
  }

  const linkedAccordions = data.map((accordionSections) => (
    <LinkedAccordian
      id={`${accordionSections.key}-help`}
      key={accordionSections.key}
      panelKey={accordionSections.key}
      title={accordionSections.title}
      onClick={() => {
        history.push(`${HELP_PAGE_URL}#${accordionSections.key}-help`);
      }}
      size={size}
      padding={accordionSections.padding}
    />
  ));

  return <>{linkedAccordions}</>;
};
